import { useCallback, useEffect, useRef, useState } from "react";
import { DataGrid } from "../../components/dataGrid/dataGrid.tsx";
import type { ChartMeta } from "../../components/dataGrid/dataChart.tsx";
import type { LineAnnotation } from "../../components/chart/charts.tsx";
import { getKioskDoorOpenings, getTemperatureSettingData, type KioskDoorOpening, type TempIQBox } from "../../api/kiosk.ts";
import { TEMPERATURE_SENSOR_SERIES } from "./temperatureChartSeries.ts";

/** Human-readable open duration, e.g. "45s", "2m 10s", "1h 5m". */
function fmtDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return rs ? `${m}m ${rs}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h ${rm}m` : `${h}h`;
}

/** Render a UTC ISO instant in the viewer's local time. */
function fmtLocal(iso: string): string {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? iso : new Date(t).toLocaleString();
}

/** Extract all integer-like numbers from one or more strings (handles "1750784526.0" → 1750784526). */
function extractNumbers(...vals: (string | null | undefined)[]): number[] {
  const out: number[] = [];
  for (const v of vals) {
    if (!v) continue;
    for (const m of v.matchAll(/\d+(?:\.\d+)?/g)) {
      const n = Math.round(Number(m[0]));
      if (Number.isFinite(n) && !out.includes(n)) out.push(n);
    }
  }
  return out;
}

/**
 * Temperature sensor graph for the Edit Kiosk tab. Renders the server-defined
 * `kiosktemperaturesensor` chart and overlays door-open events as vertical
 * dashed markers — colored to match each IQ box's temperature line (door
 * IQBoxID === TempIQBox.PiID), positioned by the event's UTC instant.
 *
 * The door window is derived from the chart's own visible time bounds, so the
 * markers always line up with whatever range the temperature series shows.
 */
export function KioskTemperatureChart({ kioskId, onReady }: { kioskId: number; onReady?: () => void }) {
  const [annotations, setAnnotations] = useState<LineAnnotation[]>([]);
  const [iqBoxes, setIqBoxes] = useState<TempIQBox[]>([]);
  const [meta, setMeta] = useState<ChartMeta | null>(null);
  const [doorEvents, setDoorEvents] = useState<KioskDoorOpening[]>([]);

  // Dedupe identical windows so re-renders / StrictMode don't refetch.
  const reqKeyRef = useRef<string>("");
  const iqBootedRef = useRef(false);

  // Load IQ box settings once (PiID + Name mapping).
  useEffect(() => {
    if (iqBootedRef.current) return;
    iqBootedRef.current = true;
    getTemperatureSettingData(kioskId).then((r) => {
      if (!r.bError && r.oData?.IqLevelSettings) setIqBoxes(r.oData.IqLevelSettings);
    }).catch(() => {});
  }, [kioskId]);

  // When chart meta arrives, fetch door events for the visible window.
  const handleMeta = useCallback((m: ChartMeta) => {
    setMeta(m);
    if (m.minTimeMs == null || m.maxTimeMs == null) {
      setDoorEvents([]);
      return;
    }
    const fromUtc = new Date(m.minTimeMs).toISOString();
    const toUtc = new Date(m.maxTimeMs).toISOString();
    const key = `${kioskId}|${fromUtc}|${toUtc}`;
    if (reqKeyRef.current === key) return;
    reqKeyRef.current = key;

    getKioskDoorOpenings(kioskId, fromUtc, toUtc)
      .then((r) => {
        if (r.bError || !r.oData) { setDoorEvents([]); return; }
        setDoorEvents(r.oData as KioskDoorOpening[]);
      })
      .catch(() => setDoorEvents([]));
  }, [kioskId]);

  // Build annotation markers whenever door events, iqBoxes, or meta change.
  useEffect(() => {
    if (!meta || doorEvents.length === 0) { setAnnotations([]); return; }

    // Build PiID → color from chart series.
    // The pivot key or series name contains the PiID (a large number like "1750784526.0").
    // iqBoxes.PiID is the authoritative mapping from IQ box to door-event IQBoxID.
    const piIds = new Set(iqBoxes.map((b) => b.PiID));
    const colorByPi = new Map<number, string>();

    for (const s of meta.series) {
      // Extract all numbers from the key and name, pick the one that matches a known PiID.
      const candidates = extractNumbers(s.key, s.name);
      let matched = candidates.find((n) => piIds.has(n));
      // If no iqBoxes loaded yet, just take the largest number (PiIDs are large; kiosk IDs are small).
      if (matched == null && candidates.length > 0) {
        matched = candidates.reduce((a, b) => (a > b ? a : b));
      }
      if (matched != null) colorByPi.set(matched, s.color);
    }

    const fallbackColor = meta.series[0]?.color ?? "var(--color-text-secondary)";
    const anns: LineAnnotation[] = [];
    for (const d of doorEvents) {
      const t = Date.parse(d.OpenedUtc);
      if (Number.isNaN(t)) continue;
      anns.push({
        atTimeMs: t,
        color: colorByPi.get(d.IQBoxID) ?? fallbackColor,
        label: "Opened",
        tooltip:
          `IQ box ${d.IQBoxID} — door opened\n` +
          `Opened: ${fmtLocal(d.OpenedUtc)}\n` +
          `Closed: ${fmtLocal(d.ClosedUtc)}\n` +
          `Open for: ${fmtDuration(d.SecondsOpened)}`,
      });
    }
    setAnnotations(anns);
  }, [meta, doorEvents, iqBoxes]);

  return (
    <DataGrid
      key={`tempsensor-${kioskId}`}
      name="kiosktemperaturesensor"
      staticFilters={[{ sColumn: "sKiosks", oValue: String(kioskId), Type: 8 }]}
      hiddenFilters={["sKiosks"]}
      autoSearch
      hideToolbar
      chartOnly
      chartShowLabels={false}
      chartAnnotations={annotations}
      onChartMeta={handleMeta}
      chartSeriesDefaults={TEMPERATURE_SENSOR_SERIES}
      onDataLoaded={onReady}
    />
  );
}
